import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { WidgetDataResult, WidgetChartConfig, WidgetClickPoint } from '../types';
import { dashboardPalette, dashboardSentimentPaletteMap, otherCategoryColor } from '../../../utils/colors';
import { mergeEChartsOption, isDateLike, formatDateCategory, formatNumber, escapeHtml } from './echartsUtils';
import { groupByKey, GroupedSeries } from './dataUtils';

interface ChartLineProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
}

export const ChartLine: React.FC<ChartLineProps> = ({ data, config, onPointClick }) => {
  const mapping = config.columnMapping;

  const { categories, seriesData, rowLookup } = useMemo(() => {
    // Key-based grouping mode
    if (mapping?.key && mapping?.xAxis && mapping?.yAxis) {
      const grouped: GroupedSeries = groupByKey(
        data.rows, mapping.xAxis, mapping.yAxis, mapping.key, mapping.label,
      );
      return {
        categories: grouped.categories,
        seriesData: grouped.series,
        rowLookup: grouped.rowLookup,
      };
    }

    // Mapped or positional single/multi-column mode
    const xCol = mapping?.xAxis || data.columns[0];
    const valueCols = mapping?.yAxis
      ? [mapping.yAxis]
      : data.columns.slice(1);

    const cats = data.rows.map((row) => String(row[xCol] ?? ''));

    const lookup = new Map<string, Map<string, typeof data.rows[0]>>();
    data.rows.forEach((row, i) => {
      const cat = cats[i];
      if (!lookup.has(cat)) lookup.set(cat, new Map());
      for (const col of valueCols) {
        lookup.get(cat)!.set(col, row);
      }
    });

    return {
      categories: cats,
      seriesData: valueCols.map((col) => ({
        name: col,
        data: data.rows.map((row) => Number(row[col]) || 0),
      })),
      rowLookup: lookup,
    };
  }, [data, mapping]);

  const colors = config.colors?.length
    ? config.colors
    : seriesData.map((s, i) => {
        const key = s.name.toUpperCase();
        if (key === 'OTHER') return otherCategoryColor;
        if (config.useSentimentColors) {
          return dashboardSentimentPaletteMap[key as keyof typeof dashboardSentimentPaletteMap] ?? dashboardPalette[i % dashboardPalette.length];
        }
        return dashboardPalette[i % dashboardPalette.length];
      });

  // Legend selection (series name -> visible). Tracked so the Y axis rescales
  // to only the visible series when the user toggles a legend item.
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({});

  const legendPosition = config.legend?.position ?? (seriesData.length > 1 ? 'bottom' : 'none');
  const legendConfig = useMemo(() => {
    const cfg: Record<string, unknown> = {
      type: 'scroll',
      show: legendPosition !== 'none',
      icon: 'circle',
      textStyle: { fontSize: 12 },
      selected: legendSelected,
    };
    if (legendPosition === 'top') cfg.top = 0;
    else if (legendPosition === 'bottom') cfg.bottom = 0;
    else if (legendPosition === 'left') { cfg.left = 0; cfg.orient = 'vertical'; }
    else if (legendPosition === 'right') { cfg.right = 0; cfg.orient = 'vertical'; }
    return cfg;
  }, [legendPosition, legendSelected]);

  const hasDateCategories = categories.length > 0 && isDateLike(categories[0]);

  const yAxisMax = useMemo(() => {
    let max = 0;
    for (const s of seriesData) {
      if (legendSelected[s.name] === false) continue;
      for (const v of s.data) {
        if (v > max) max = v;
      }
    }
    return max > 0 ? max + (max / 10) : undefined;
  }, [seriesData, legendSelected]);

  const option = useMemo(() => {
    const series = seriesData.map((s) => ({
      name: s.name,
      type: 'line' as const,
      data: s.data,
      smooth: true,
      symbolSize: 6,
      label: {
        show: true,
        position: 'top' as const,
        fontSize: 11,
        formatter: (params: any) => {
          const val = params.value;
          return typeof val === 'number' ? formatNumber(val) : String(val ?? '');
        },
      },
    }));

    const base = {
      color: colors,
      tooltip: {
        trigger: 'axis' as const,
        appendToBody: true,
        formatter: (params: any) => {
          const title = escapeHtml(params[0]?.axisValueLabel ?? params[0]?.name ?? '');
          const lines = (params as any[]).map((p: any) => {
            const val = typeof p.value === 'number' ? formatNumber(p.value) : escapeHtml(p.value);
            return `${p.marker}${escapeHtml(p.seriesName)}: <b>${val}</b>`;
          });
          return [title, ...lines].join('<br/>');
        },
      },
      legend: legendConfig,
      grid: {
        left: 10,
        right: 10,
        top: legendPosition === 'top' ? 40 : 10,
        bottom: legendPosition === 'bottom' ? 50 : 20,
        containLabel: true,
      },
      xAxis: {
        type: 'category' as const,
        data: categories,
        name: config.labels?.x || '',
        nameLocation: 'end' as const,
        nameGap: 8,
        axisLabel: {
          fontSize: 11,
          color: '#666',
          rotate: categories.length > 6 ? 30 : 0,
          overflow: 'truncate' as const,
          ...(hasDateCategories && { formatter: formatDateCategory }),
        },
      },
      yAxis: {
        type: 'value' as const,
        name: config.labels?.y || '',
        max: yAxisMax,
        axisLabel: { fontSize: 11, color: '#666', formatter: (val: number) => formatNumber(val) },
        splitLine: {
          show: config.showGrid !== false,
          lineStyle: { color: '#f0f0f0' },
        },
      },
      series,
    };
    return mergeEChartsOption(base, config.echartsOption);
  }, [categories, seriesData, colors, legendConfig, legendPosition, hasDateCategories, config, yAxisMax]);

  const onEvents = useMemo(() => {
    const events: Record<string, (params: any) => void> = {
      legendselectchanged: (params: any) => {
        setLegendSelected({ ...(params.selected ?? {}) });
      },
    };
    if (onPointClick) {
      events.click = (params: any) => {
        const cat = String(params.name ?? '');
        const seriesName = String(params.seriesName ?? '');
        const row = rowLookup.get(cat)?.get(seriesName);
        onPointClick({
          label: seriesName,
          key: cat,
          value: params.value ?? 0,
          extra: row ? { ...row } as Record<string, string | number | boolean | null> : undefined,
        });
      };
    }
    return events;
  }, [onPointClick, rowLookup]);

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height: '100%' }}
      opts={{ renderer: 'svg' }}
      onEvents={onEvents}
    />
  );
};
